import { useState, useEffect } from 'react'
import { networkClient } from '../network/client'

interface DebugPuzzleData {
  puzzleId: string
  type: 'debug'
  difficulty: string
  title: string
  description: string
  code: string[]
}

interface DebugPuzzlePanelProps {
  puzzle: DebugPuzzleData
  onClose: () => void
}

export default function DebugPuzzlePanel({ puzzle, onClose }: DebugPuzzlePanelProps) {
  const [selectedLine, setSelectedLine] = useState<number | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [hint, setHint] = useState<string | null>(null)
  const [hintsUsed, setHintsUsed] = useState(0)
  const [solved, setSolved] = useState(false)
  const [solvedData, setSolvedData] = useState<{
    points: number
    message: string
    bugLineIndex: number
    bugExplanation: string
    fixedLine: string
    stabilityRestored?: number
  } | null>(null)

  useEffect(() => {
    const room = networkClient.getGameRoom()
    if (!room) return

    const handleWrong = (data: { message: string }) => {
      setFeedback(data.message)
      setTimeout(() => setFeedback(null), 3000)
    }

    const handleHint = (data: { hint: string; hintsUsed: number }) => {
      setHint(data.hint)
      setHintsUsed(data.hintsUsed)
    }

    const handleSolved = (data: any) => {
      if (data.puzzleId === puzzle.puzzleId && data.type === 'debug') {
        setSolved(true)
        setSolvedData(data)
      }
    }

    room.onMessage('puzzle_wrong', handleWrong)
    room.onMessage('puzzle_hint', handleHint)
    room.onMessage('puzzle_solved', handleSolved)

    return () => {}
  }, [puzzle.puzzleId])

  const handleLineClick = (index: number) => {
    setSelectedLine(index)
  }

  const handleSubmit = () => {
    if (selectedLine === null) {
      setFeedback('Najpierw wybierz linie z bledem!')
      setTimeout(() => setFeedback(null), 2000)
      return
    }

    const room = networkClient.getGameRoom()
    if (room) {
      room.send('submit_debug_answer', {
        puzzleId: puzzle.puzzleId,
        selectedLineIndex: selectedLine,
      })
    }
  }

  const requestHint = () => {
    const room = networkClient.getGameRoom()
    if (room) {
      room.send('request_hint', { puzzleId: puzzle.puzzleId })
    }
  }

  const getDifficultyColor = (diff: string) => {
    switch (diff) {
      case 'easy': return 'text-green-400'
      case 'medium': return 'text-yellow-400'
      case 'hard': return 'text-red-400'
      default: return 'text-white'
    }
  }

  const getDifficultyLabel = (diff: string) => {
    switch (diff) {
      case 'easy': return 'Latwa'
      case 'medium': return 'Srednia'
      case 'hard': return 'Trudna'
      default: return diff
    }
  }

  if (solved && solvedData) {
    return (
      <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
        <div className="bg-gray-800 border-2 border-emerald-neon rounded-xl p-8 max-w-2xl text-center">
          <h2 className="text-3xl font-bold text-emerald-neon mb-4">Blad znaleziony!</h2>
          <p className="text-xl text-white mb-4">{solvedData.message}</p>
          <div className="text-4xl font-bold text-yellow-400 mb-2">+{solvedData.points} pkt</div>
          {solvedData.stabilityRestored && (
            <div className="text-lg text-emerald-neon mb-4">+{solvedData.stabilityRestored}% stabilnosci</div>
          )}

          <div className="mb-6 p-4 bg-gray-900 rounded-lg text-left">
            <p className="text-gray-400 mb-3">Poprawiony kod:</p>
            <div className="font-mono text-sm">
              {puzzle.code.map((line, idx) => (
                <div
                  key={idx}
                  className={`py-1 px-2 rounded ${
                    idx === solvedData.bugLineIndex
                      ? 'bg-emerald-neon/20 border-l-4 border-emerald-neon'
                      : ''
                  }`}
                >
                  <span className="text-gray-500 mr-3">{idx + 1}</span>
                  {idx === solvedData.bugLineIndex ? (
                    <>
                      <span className="line-through text-red-400">{line}</span>
                      <br />
                      <span className="text-gray-500 mr-3">&nbsp;</span>
                      <span className="text-emerald-neon">{solvedData.fixedLine}</span>
                    </>
                  ) : (
                    <span className="text-gray-300">{line}</span>
                  )}
                </div>
              ))}
            </div>
            <p className="text-yellow-400 mt-4 text-sm">
              <strong>Wyjasnienie:</strong> {solvedData.bugExplanation}
            </p>
          </div>

          <button
            onClick={onClose}
            className="px-8 py-3 bg-emerald-neon text-gray-900 font-bold rounded-lg hover:bg-emerald-400"
          >
            Kontynuuj
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 border-2 border-gray-600 rounded-xl p-6 max-w-2xl w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-white">{puzzle.title}</h2>
            <p className={`text-sm ${getDifficultyColor(puzzle.difficulty)}`}>
              Trudnosc: {getDifficultyLabel(puzzle.difficulty)}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl">&times;</button>
        </div>

        {/* Instructions */}
        <p className="text-gray-300 mb-4">{puzzle.description}</p>

        <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-3 mb-4">
          <p className="text-yellow-400 text-sm">
            Kliknij na linie zawierajaca blad, a nastepnie nacisnij "Zglos blad"
          </p>
        </div>

        {/* Code display */}
        <div className="bg-gray-900 rounded-lg p-4 mb-6 font-mono text-sm">
          {puzzle.code.map((line, idx) => (
            <div
              key={idx}
              onClick={() => handleLineClick(idx)}
              className={`py-2 px-3 cursor-pointer rounded transition-all ${
                selectedLine === idx
                  ? 'bg-red-600/30 border-l-4 border-red-500'
                  : 'hover:bg-gray-800 border-l-4 border-transparent'
              }`}
            >
              <span className="text-gray-500 mr-4 select-none">{idx + 1}</span>
              <span className={selectedLine === idx ? 'text-red-300' : 'text-gray-300'}>
                {line}
              </span>
            </div>
          ))}
        </div>

        {/* Selected line indicator */}
        {selectedLine !== null && (
          <div className="mb-4 p-3 bg-gray-700 rounded-lg">
            <span className="text-gray-400">Wybrana linia: </span>
            <span className="text-red-400 font-mono">{selectedLine + 1}: {puzzle.code[selectedLine]}</span>
          </div>
        )}

        {/* Submit button */}
        <button
          onClick={handleSubmit}
          disabled={selectedLine === null}
          className="w-full mb-4 px-6 py-3 bg-red-600 text-white font-bold rounded-lg hover:bg-red-500
                     disabled:bg-gray-600 disabled:cursor-not-allowed"
        >
          Zglos blad
        </button>

        {/* Feedback */}
        {feedback && (
          <div className="mb-4 p-3 bg-red-900/50 border border-red-500 text-red-300 rounded-lg">
            {feedback}
          </div>
        )}

        {/* Hint */}
        {hint && (
          <div className="mb-4 p-3 bg-blue-900/50 border border-blue-500 text-blue-300 rounded-lg">
            <strong>Podpowiedz:</strong> {hint}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-between items-center">
          <button
            onClick={requestHint}
            disabled={hintsUsed >= 3}
            className="px-4 py-2 text-gray-400 hover:text-yellow-400 disabled:opacity-50"
          >
            Popros o podpowiedz ({hintsUsed}/3)
          </button>

          <div className="text-gray-500 text-sm">
            Nagroda: {puzzle.difficulty === 'easy' ? 150 : puzzle.difficulty === 'medium' ? 250 : 500} pkt
          </div>
        </div>
      </div>
    </div>
  )
}
